import * as React from 'react';
import HelpDocument from '../models/helpDocument';
import useHelp from './useHelp';
import * as styles from '../components/help/help.module.scss';
import { NavLink, useParams } from 'react-router-dom';
import { HelpDocumentsContext } from '../shared/help-documents-context';
import HelpDocumentContent from '../components/management/clientHelpDocuments/help-documentation-content';
import Paths from '../models/enums/paths';
import { TreeData } from '@atlaskit/tree';
import useTimedAlerts from './useTimedAlerts';

/**
 * Custom hook for the help page
 */
const useHelpDocuments = () => {
  const {
    helpDocuments,
    globalHelpDocuments,
    loading,
    activeDocumentId,
    clientList,
    dispatch,
    errorMessage,
    successMessage
  } = React.useContext(HelpDocumentsContext);
  const { clientId } = useParams<{ clientId: string }>();
  const { errorMessage: message, setErrorMessage } = useTimedAlerts();
  const {
    getHelpDocuments,
    getGlobalHelpDocuments,
    updateDocumentTreeAfterFetch,
    getLatestHelpDocuments,
    deleteGlobalHelpDocument,
    deleteClientHelpDocument,
    updateClientHelpDocumentsSortOrder,
    updateDocumentTreeAfterDelete,
    updateGlobalHelpDocumentsSortOrder,
    getChildDocsToRenderEdit,
    documentsWithoutRoot
  } = useHelp();

  React.useEffect(() => {
    dispatch({ errorMessage: message });
  }, [message]);

  // Clears success message after interval if success message is present on page load
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (successMessage) {
        dispatch({ successMessage: '' });
      }
    }, 5000);
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  // Clears error message after it appears in context
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (errorMessage) {
        dispatch({ errorMessage: '' });
      }
    }, 5000);
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [errorMessage]);

  /**
   * Fetches the help documents from the backend and puts them in context
   */
  const fetchClientHelpDocuments = () => {
    getHelpDocuments(Number(clientId))
      .then((helpDocumentsRes: HelpDocument[]) => {
        dispatch({ helpDocuments: createDocumentsList(helpDocumentsRes) });
      })
      .catch(() => dispatch({ loading: false }));
  };

  /**
   * Fetches the global help documents from the backend and puts them in context
   * @param update - Boolean indicate if the list of documents is being updated
   * @param globalContext - Indicates whether to use helpDocuments or globalHelpDocuments
   */
  const fetchGlobalHelpDocuments = (update?: boolean, globalContext?: boolean) => {
    getGlobalHelpDocuments()
      .then((response: any) => {
        if (!globalContext) {
          dispatch({
            helpDocuments: update
              ? updateDocumentTreeAfterFetch(helpDocuments, response)
              : createDocumentsList(response)
          });
        } else {
          dispatch({ globalHelpDocuments: createDocumentsList(response) });
        }
      })
      .catch(() => dispatch({ loading: false }));
  };

  /**
   * Fetches the help documents with the latest revisions from the backend and puts them in context
   */
  const fetchHelpDocumentsWithRevisions = (update?: boolean) => {
    return getLatestHelpDocuments(Number(clientId))
      .then((response: any) => {
        dispatch({
          helpDocuments: update ? updateDocumentTreeAfterFetch(helpDocuments, response) : createDocumentsList(response)
        });
      })
      .catch(() => dispatch({ loading: false }));
  };

  /**
   * Deletes the selected global document
   * @param documentId - Identifier of the document to be deleted (snippetId)
   */
  const triggerDeleteGlobalHelpDocument = (documentId: number) => {
    dispatch({ loading: true });
    return deleteGlobalHelpDocument(documentId)
      .then(() => {
        const documentTree = updateDocumentTreeAfterDelete(helpDocuments, documentId);
        dispatch({
          helpDocuments: documentTree,
          activeDocumentId: Number(documentTree.items['-1'].children[0]),
          loading: false
        });
        return documentTree;
      })
      .catch(error => {
        dispatch({ loading: false });
        dispatch({
          errorMessage:
            (error.responseJSON && error.responseJSON.message) ||
            'Error deleting document, please try again or contact your System Administrator.'
        });
      });
  };

  /**
   * Deletes the selected client help document
   * @param documentId - Identifier of the document to be deleted (snippetId)
   */
  const triggerDeleteClientHelpDocument = (documentId: number) => {
    dispatch({ loading: true });
    return deleteClientHelpDocument(clientId, documentId)
      .then(() => {
        const documentTree = updateDocumentTreeAfterDelete(helpDocuments, documentId);
        dispatch({
          helpDocuments: documentTree,
          activeDocumentId: Number(documentTree.items['-1'].children[0]),
          loading: false
        });
        return documentTree;
      })
      .catch(error => {
        dispatch({ loading: false });
        dispatch({
          errorMessage:
            (error.responseJSON && error.responseJSON.message) ||
            'Error deleting document, please try again or contact your System Administrator.'
        });
        throw error;
      });
  };

  /**
   * Updates the sort order of the client help documents
   * @param snippetGroupId The identifier of the document that will be updated
   * @param newParentGroupId The identifier of the parent document that it will be moved to
   * @param sourceGroupIds All the children and subchildren of the document that was moved
   * @param destinationGroupId The identifier of the next document in the list
   * @param prevState - Previous state of the help documents to be used in case of an error
   */
  const triggerUpdateClientDocumentsSortOrder = (
    snippetGroupId: number,
    newParentGroupId: number,
    sourceGroupIds: number[],
    destinationGroupId: number,
    prevState: TreeData
  ) => {
    updateClientHelpDocumentsSortOrder(
      Number(clientId),
      snippetGroupId,
      newParentGroupId,
      sourceGroupIds,
      destinationGroupId
    ).catch(error => {
      setErrorMessage(error.responseJSON.message);
      dispatch({ helpDocuments: prevState });
    });
  };

  /**
   * Updates the sort order of the global help documents
   * @param snippetGroupId The identifier of the document that will be updated
   * @param newParentGroupId The identifier of the parent document that it will be moved to
   * @param sourceGroupIds All the children and subchildren of the document that was moved
   * @param destinationGroupId The identifier of the next document in the list
   * @param prevState - Previous state of the help documents to be used in case of an error
   */
  const triggerUpdateGlobalDocumentsSortOrder = (
    snippetGroupId: number,
    newParentGroupId: number,
    sourceGroupIds: number[],
    destinationGroupId: number,
    prevState: TreeData
  ) => {
    updateGlobalHelpDocumentsSortOrder(snippetGroupId, newParentGroupId, sourceGroupIds, destinationGroupId).catch(
      error => {
        setErrorMessage(error.responseJSON.message);
        dispatch({ helpDocuments: prevState });
      }
    );
  };

  /**
   * Changes the active document and checks if it matches the current document
   * @param params Parameters from the nav-link
   * @param location Location parameters from the nav-link
   * @param activeDocumentId Id of the document that the user selected
   */
  const changeActiveDoc = (params: any, location: any, activeDocumentId: number) => {
    if (params && location.state) {
      if (activeDocumentId === location.state.id) {
        dispatch({ activeDocumentId });
        return true;
      }
    }
    return false;
  };

  /**
   * Creates the document tree using the Atlassian Tree structure
   * @param documents - Array of all help documents
   */
  const createDocumentsList = (documents: HelpDocument[]) => {
    const tree: TreeData = {
      rootId: '-1',
      items: {
        '-1': {
          id: '-1',
          hasChildren: true,
          isExpanded: true,
          isChildrenLoading: false,
          children: documents.filter(doc => doc.parentGroupId === 0).map(doc => doc.snippetGroupId)
        }
      }
    };
    const items = {};
    documents.forEach(document => {
      const childrenList = documents
        .filter(doc => doc.parentGroupId === document.snippetGroupId)
        .map(doc => doc.snippetGroupId);
      items[document.snippetGroupId] = {
        id: document.snippetGroupId,
        hasChildren: childrenList.length > 0,
        isExpanded: true,
        isChildrenLoading: false,
        data: document,
        children: childrenList
      };
    });
    tree.items = { ...tree.items, ...items };
    return tree;
  };

  /**
   * Renders the help documents
   * @returns Array of JSX elements to render the help document contents
   */
  const renderHelpContent = (isSearching: boolean) => {
    if (helpDocuments.rootId && activeDocumentId) {
      return getChildDocsToRenderEdit(activeDocumentId, helpDocuments).map((doc: HelpDocument) => (
        <HelpDocumentContent
          key={`help-doc-${doc.snippetGroupId}`}
          document={doc}
          activeDoc={activeDocumentId}
          isSearching={isSearching}
        />
      ));
    }
  };

  /**
   * Renders the preview for documents while searching
   * @param searchQuery The search query the user typed into the search bar
   * @param isSearching Boolean indicating whether or not the user is currently searching
   * @param setIsSearching Function to indicate whether the user is currently searching or not
   * @returns An array of JSX elements with the preview of the documents
   */
  const renderPreviews = (searchQuery: string, isSearching: boolean, setIsSearching: (searching: boolean) => void) => {
    if (!isSearching) {
      return;
    }
    /** Regex that uses the search query and an ignore case modifier */
    const regex = RegExp(searchQuery, 'i');
    /** Array of documents where either the title or the content match the search query */
    const docsToPreview = documentsWithoutRoot(helpDocuments)
      .map(doc => doc.data)
      .filter((doc: HelpDocument) => regex.test(doc.title) || regex.test(doc.content));

    if (docsToPreview.length === 0) {
      return <span>No Matching Documents</span>;
    }

    const currentRoute = clientId ? `/clients/${clientId}/edit/help` : Paths.GlobalHelpDocuments;

    return docsToPreview.map((doc: HelpDocument) => (
      <NavLink
        key={`help-preview-${doc.snippetGroupId}`}
        className={styles.previewContainer}
        onClick={() => setIsSearching(false)}
        to={{
          pathname: `${currentRoute}/${doc.snippetGroupId}`,
          state: { id: doc.snippetGroupId }
        }}
        isActive={(param, location) => {
          return changeActiveDoc(param, location, doc.snippetGroupId);
        }}
      >
        <span>{doc.title}</span>
        <div className={styles.preview}>
          {/** activeDoc is set to 0 in order to avoid unnecessary scrolling */}
          <HelpDocumentContent
            key={`help-doc-${doc.snippetGroupId}`}
            document={doc}
            activeDoc={0}
            isSearching={isSearching}
          />
        </div>
      </NavLink>
    ));
  };

  return {
    loading,
    helpDocuments,
    globalHelpDocuments,
    activeDocumentId,
    clientList,
    dispatch,
    errorMessage,
    successMessage,
    fetchClientHelpDocuments,
    fetchGlobalHelpDocuments,
    fetchHelpDocumentsWithRevisions,
    changeActiveDoc,
    renderHelpContent,
    renderPreviews,
    createDocumentsList,
    updateClientDocumentsSortOrder: triggerUpdateClientDocumentsSortOrder,
    updateGlobalDocumentsSortOrder: triggerUpdateGlobalDocumentsSortOrder,
    deleteGlobalHelpDocument: triggerDeleteGlobalHelpDocument,
    deleteClientHelpDocument: triggerDeleteClientHelpDocument
  };
};
export default useHelpDocuments;
